import { create } from 'zustand'
import { devtools, persist, createJSONStorage } from 'zustand/middleware'
import { getApiClient, getStudioClient } from './clientsStore'
import { handleHttpErrors } from '../shared'
import { type Bot, isApiError } from '@botpress/client'
import { trackEvent } from '../providers'
import { queryClient } from '../providers/ReactQuery'
import { showDialog, showErrorToast } from '@bpinternal/ui-kit-next'
import { BotQuotaExceededDialog } from '~/features/bots/components'
import _ from 'lodash'
import defaultBotJson from '../../public/default_bot_assets/bot.json'
import { publishBotViaBotJson, installIntegrationsAndPlugins } from '../features/bots/services/onboarding'
import { setWorkspacePreference } from '~/services/preferences/workspacePreferences'

export type Status = 'created' | 'imported-template' | 'integrations-added' | 'deployed'

type BotCreationState = {
  botsBeingCreated: Record<string, Status>
}

type BotCreationActions = {
  createBot: (props: {
    workspaceId: string
    name?: string
    onBotCreated?: (bot: Bot) => void
    onBotError?: (error: any) => void
    onStatusChange?: (status: Status) => void
  }) => void
  createAdkBot: (props: {
    workspaceId: string
    name: string
    onBotCreated?: (bot: Bot) => void
    onBotError?: (error: any) => void
  }) => void
  updateBotStatus: (botId: string, status: Status, callback?: (status: Status) => void) => void
}

export const NEW_BOT_NAME = 'New Agent'

export const useBotCreationStore = create<BotCreationState & BotCreationActions>()(
  devtools(
    persist(
      (set, get) => ({
        botsBeingCreated: {},
        createBot: async ({ workspaceId, name = NEW_BOT_NAME, onBotCreated, onBotError, onStatusChange }) => {
          const apiClient = getApiClient({ workspaceId })
          const response = await apiClient
            .createBot({ name })
            .catch(handleCreateBotError(workspaceId, onBotError ?? (() => {})))

          if (!response?.bot) {
            return
          }

          await getApiClient({ workspaceId, botId: response.bot.id }).createKnowledgeBase({
            name: 'Default Knowledge Base',
            tags: { oldKbId: 'kb-default' },
          })

          const updateBotStatus = (status: Status) => get().updateBotStatus(response.bot.id, status, onStatusChange)

          trackEvent({ type: 'create_bot', botId: response.bot.id })
          queryClient.invalidateQueries()

          await setWorkspacePreference(
            { path: 'isOnboarding/$botId', params: { botId: response.bot.id }, value: true },
            workspaceId
          )
          updateBotStatus('created')
          onBotCreated?.(response.bot)

          const studioClient = getStudioClient({ botId: response.bot.id, workspaceId })
          const fileUrl = `${window.location.origin}/default_bot_assets/default-bot.bpz`

          await studioClient.post('/import', { fileUrl })
          updateBotStatus('imported-template')

          await installIntegrationsAndPlugins(workspaceId, response.bot.id)
          updateBotStatus('integrations-added')

          await publishBotViaBotJson(workspaceId, response.bot.id, _.cloneDeep(defaultBotJson))
          updateBotStatus('deployed')

          queryClient.invalidateQueries()

          //TODO: Make this store persistent

          return apiClient.getBot({ id: response.bot.id }).catch(handleHttpErrors)
        },
        createAdkBot: async ({ workspaceId, name, onBotCreated, onBotError }) => {
          const apiClient = getApiClient({ workspaceId })
          const response = await apiClient
            .createBot({ name, tags: { runtime: 'adk' } })
            .catch(handleCreateBotError(workspaceId, onBotError ?? (() => {})))

          if (!response?.bot) {
            return
          }

          trackEvent({ type: 'create_adk_bot', botId: response.bot.id })
          queryClient.invalidateQueries()

          await setWorkspacePreference(
            { path: 'isAdkOnboarding/$botId', params: { botId: response.bot.id }, value: true },
            workspaceId
          )

          onBotCreated?.(response.bot)

          return response.bot
        },
        updateBotStatus: (botId, status, callback) =>
          set((state) => {
            callback?.(status)
            return {
              botsBeingCreated: {
                ...state.botsBeingCreated,
                [botId]: status,
              },
            }
          }),
      }),
      {
        name: 'bot-creation-storage',
        storage: createJSONStorage(() => sessionStorage),
      }
    )
  )
)

const handleCreateBotError = (workspaceId: string, onBotError: (error: any) => void) => (error: any) => {
  onBotError(error)
  if (isApiError(error) && error.type === 'QuotaExceeded') {
    showDialog(({ close }) => <BotQuotaExceededDialog workspaceId={workspaceId} close={close} />, {
      title: 'Limit Exceeded',
      description: 'You have reached the limit of bots you can create. Please upgrade your plan to create more bots',
    })
  } else {
    showErrorToast(`An error occurred while creating the chatbot: ${error.message}`)
  }
}
